import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import Orb from '@/blocks/Backgrounds/Orb/Orb';
import { motion } from 'framer-motion';

interface HeroSectionProps {
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ title, subtitle, ctaText, ctaLink }) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <section className="bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 text-white py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-accent-500 opacity-10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary-500 opacity-10 rounded-full blur-3xl"></div>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">{title}</h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">{subtitle}</p>
            <Button asChild size="lg" variant="accent">
              <Link to={ctaLink}>
                {ctaText}
              </Link>
            </Button>
          </div>
          <div className="hidden lg:flex justify-center items-center relative">
            {isHovering && (
              <motion.div 
                className="absolute bg-white/10 backdrop-blur-sm rounded-xl px-6 py-2 z-10 flex justify-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
                style={{ 
                  width: '240px',
                  bottom: 'calc(50% + 6rem)'
                }}
              >
                <div className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2 w-0 h-0 
                  border-l-[6px] border-l-transparent
                  border-t-[6px] border-t-white/10
                  border-r-[6px] border-r-transparent">
                </div>
                <div className="relative h-5 flex items-center justify-center">
                  <span className="text-sm font-medium text-white whitespace-nowrap">
                    {"Hello! How may I help you?".split('').map((char, index) => (
                      <motion.span
                        key={index}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{
                          duration: 0.05,
                          delay: 0.3 + index * 0.05,
                        }}
                      >
                        {char}
                      </motion.span>
                    ))}
                  </span>
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      duration: 0.8,
                      delay: 0.3 + "Hello! How may I help you?".length * 0.05,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                    className="ml-1 inline-block rounded-sm w-[2px] h-3.5 bg-accent-400"
                  />
                </div>
              </motion.div>
            )}
            <div 
              className="relative w-48 h-48"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
                <Orb hue={180} hoverIntensity={0.3} rotateOnHover={true} />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div 
                    style={{
                      filter: 'drop-shadow(0 0 15px rgba(255,255,255,0.5)) drop-shadow(0 0 30px rgba(103,232,249,0.3))'
                    }}
                  >
                    <svg
                      width="96"
                      height="96"
                      viewBox="0 0 800 907.58743"
                      className="opacity-95"
                    >
                      <defs>
                        <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9"/>
                          <stop offset="50%" stopColor="#67e8f9" stopOpacity="0.8"/>
                          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.9"/>
                        </linearGradient>
                      </defs>
                      <g transform="matrix(10,0,0,10,1.5e-5,0)">
                        <g transform="matrix(3.6347115,0,0,3.6347115,-18.173559,-14.538846)" 
                           fill="url(#iconGradient)">
                          <path d="M 22.19,4 H 9.81 C 7.16,4 5,6.16 5,8.81 V 21.19 C 5,23.84 7.16,26 9.81,26 h 8.03 c 0.55,0 1,-0.45 1,-1 0,-0.55 -0.45,-1 -1,-1 H 9.81 C 8.26,24 7,22.74 7,21.19 V 8.81 C 7,7.26 8.26,6 9.81,6 H 22.19 C 23.74,6 25,7.26 25,8.81 v 17.17 l -2.4,-1.79 c -0.44,-0.33 -1.07,-0.24 -1.4,0.21 -0.33,0.44 -0.24,1.07 0.21,1.4 l 4,2.97 c 0.18,0.13 0.39,0.2 0.6,0.2 0.15,0 0.31,-0.04 0.45,-0.11 0.34,-0.17 0.55,-0.52 0.55,-0.89 V 8.81 C 27.01,6.16 24.85,4 22.2,4 Z" />
                          <path d="M 13,12.9 V 11 c 0,-0.55 -0.45,-1 -1,-1 -0.55,0 -1,0.45 -1,1 v 1.9 c 0,0.55 0.45,1 1,1 0.55,0 1,-0.45 1,-1 z" />
                          <path d="m 20,10 c -0.55,0 -1,0.45 -1,1 v 1.9 c 0,0.55 0.45,1 1,1 0.55,0 1,-0.45 1,-1 V 11 c 0,-0.55 -0.45,-1 -1,-1 z" />
                          <path d="m 19.29,17.27 c -2.27,2.29 -4.31,2.29 -6.58,0 -0.39,-0.39 -1.02,-0.4 -1.41,0 -0.39,0.39 -0.4,1.02 0,1.41 1.52,1.54 3.11,2.33 4.71,2.33 1.6,0 3.19,-0.78 4.71,-2.33 0.39,-0.39 0.38,-1.03 0,-1.41 -0.39,-0.39 -1.03,-0.38 -1.41,0 z" />
                        </g>
                      </g>
                    </svg>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
